import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getEntry } from "core-integration/src/store/entries/reducers";
import { getFutureEvents } from "core-integration/src/store/events/reducers";
import { getRules } from "core-integration/src/store/game/reducers";
import { getCreateH2HLeagueError } from "core-integration/src/store/leagues/reducers";
import { createH2HLeague } from "core-integration/src/store/leagues/thunks";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { ILoggedInPlayer } from "core-integration/src/store/player/types";
import range from "lodash/range";
import { ErrorAlert } from "plos/src/components/alerts";
import { Button } from "plos/src/components/buttons/Button";
import { FormSelect, FormTextField } from "plos/src/components/forms";
import HorizontalDivider from "plos/src/components/HorizontalDivider";
import SectionHeader from "plos/src/components/SectionHeader";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import { useLocalizedNavigate } from "plos/src/hooks/useLocalizedNavigate";
import { rightSidebarLayout } from "plos/src/layouts";
import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useTrackingContext } from "../../../contexts/TrackingContext";
import HelmetHeadLeagues from "../../leagues/HelmetHeadLeagues";
import {
  formElementWrapper,
  formItem,
  gridDivider,
  leagueGridLayout,
  leaguesMainContent,
} from "../leaguesAndCups.css";

const CreateH2H = () => {
  const navigate = useLocalizedNavigate();

  const { t } = useTranslation("leaguesAndCups");

  const { fireClickTrackEvent, firePageViewEvent, logBranchEvent } =
    useTrackingContext();
  const dispatch = useDispatch<ThunkDispatch>();

  const player = useSelector(
    (state: RootState) => getPlayerData(state) as ILoggedInPlayer,
  ); // enforced by EntryRoute
  const playerEntry = player.entry;
  const entry = useSelector((state: RootState) => getEntry(state, playerEntry));

  const events = useSelector(getFutureEvents);
  const rules = useSelector(getRules);
  const error = useSelector(getCreateH2HLeagueError);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [koRounds, setKoRounds] = useState(0);
  const [maxEntries, setMaxEntries] = useState(0);
  const [startEvent, setStartEvent] = useState(
    events.length ? events[0].id : 1,
  );

  const handleSetName = (val: string) => {
    setName(val);
  };
  const handleSetStartEvent = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStartEvent(parseInt(e.currentTarget.value, 10));
  };
  const handleSetMaxEntries = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setMaxEntries(parseInt(e.currentTarget.value, 10));
  };
  const handleSetKoRounds = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setKoRounds(parseInt(e.currentTarget.value, 10));
  };

  const noEvents = !events.length;

  useEffect(() => {
    firePageViewEvent("fantasy leagues & cups", "create league head to head");
  }, []);

  useEffect(() => {
    if (noEvents) {
      navigate("/", { replace: true });
    }
  }, [noEvents, navigate]);

  const maximumKoRounds = () => {
    const remainingEvents = events.length;
    if (remainingEvents <= 1) {
      return 0;
    }
    return Math.min(
      rules.league_max_ko_rounds_private_h2h,
      remainingEvents - 1,
    );
  };

  const minimumSize = (koRounds: number) =>
    koRounds ? Math.pow(2, koRounds) : 2;

  const validStartingEvents = (koRounds: number) =>
    koRounds ? events.slice(0, -koRounds) : events;

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    const leagueCreated = await dispatch(
      createH2HLeague({
        ko_rounds: koRounds || null,
        max_entries: maxEntries || null,
        name,
        start_event: startEvent,
      }),
    );
    const h2hLeagueCreated = {
      event_category: "fantasy league",
      event_component: "fantasy classic clicks",
      event_detail: "create h2h cta",
      event_type: "create league / cup",
    };
    fireClickTrackEvent(h2hLeagueCreated, "fantasy create h2h league");
    if (leagueCreated) {
      logBranchEvent("Create league – H2H", h2hLeagueCreated);
    }
    navigate("/leagues");
  };

  const disabled = !name || !startEvent || isSubmitting;

  const nonFieldError = error?.badRequest?.non_field_errors?.[0];
  const errorMessage =
    nonFieldError?.code === "max_private_entries"
      ? `You may only enter ${rules?.league_join_private_max} invitational leagues`
      : JSON.stringify(error);

  if (noEvents || !entry) {
    return null;
  }

  return (
    <>
      <HelmetHeadLeagues />
      <div className={rightSidebarLayout}>
        <SurfaceContainer>
          <div className={leaguesMainContent}>
            {nonFieldError && <ErrorAlert>{errorMessage}</ErrorAlert>}
            <SectionHeader
              title={t("createH2H.title", "Create a new Head-to-Head League")}
            />
            <form onSubmit={handleSubmit}>
              <div className={leagueGridLayout}>
                <div className={formElementWrapper}>
                  <FormTextField
                    label={t("createH2H.label.leagueName", "League Name")}
                    description={t("characterLimit", "Maximum 30 characters")}
                    value={name}
                    onChange={(val) => handleSetName(val)}
                    placeholder={t("enterNameHere", "Enter Name Here")}
                    maxLength={30}
                  />
                  <p>
                    <Trans ns="leaguesAndCups" i18nKey="createH2H.nameWarning">
                      Please think carefully before entering your league name.
                      Leagues with names that are deemed inappropriate or
                      offensive may result in your account being deleted. Please
                      refer to the Terms & Conditions of entry for more
                      information.
                    </Trans>
                  </p>
                </div>

                <div className={gridDivider}>
                  <HorizontalDivider />
                </div>

                <div className={formItem}>
                  <FormSelect
                    id="scoring-starts"
                    label={t("createH2H.label.scoringStarts", "Scoring starts")}
                    value={startEvent}
                    onChange={handleSetStartEvent}
                  >
                    {validStartingEvents(koRounds).map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.name}
                      </option>
                    ))}
                  </FormSelect>
                </div>

                <div className={formItem}>
                  <FormSelect
                    id="maximum-size"
                    value={maxEntries}
                    onChange={handleSetMaxEntries}
                    label={t("createH2H.label.maximumSize", "Maximum size")}
                  >
                    <option value={0} aria-selected={!maxEntries}>
                      {t("createH2H.options.unlimited", "Unlimited")}
                    </option>
                    {range(
                      minimumSize(koRounds),
                      rules.league_max_size_private_h2h + 1,
                    ).map((num) => (
                      <option key={num} value={num}>
                        {num}
                      </option>
                    ))}
                  </FormSelect>
                </div>

                {maximumKoRounds() > 0 && (
                  <div className={formItem}>
                    <FormSelect
                      id="ko-rounds"
                      value={koRounds}
                      onChange={handleSetKoRounds}
                      label={t(
                        "createH2H.label.knockoutRounds",
                        "Knockout rounds",
                      )}
                    >
                      <option value={0} aria-selected={!koRounds}>
                        {t("createH2H.options.none", "None")}
                      </option>
                      {range(1, maximumKoRounds() + 1).map((num) => (
                        <option key={num} value={num}>
                          {num}
                        </option>
                      ))}
                    </FormSelect>
                  </div>
                )}

                <div className={formItem}>
                  <Button type="submit" isDisabled={disabled}>
                    {isSubmitting
                      ? t("isSubmitting.pleaseWait", "Please wait...")
                      : t("isSubmitting.createLeague", "Create League")}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </SurfaceContainer>
      </div>
    </>
  );
};
export default CreateH2H;
